<template>
  <div class="ar-login-page">
    <div class="ar-login-card">
      <div class="text-center mb-4">
        <a href="/portal" class="d-inline-block mb-3">
          <img src="/assets/images/logo.svg" alt="Asher" class="ar-login-logo"/>
        </a>
        <h2 class="ar-login-title mb-1">Driver sign in</h2>
        <p class="text-muted mb-0">Welcome back — choose how you'd like to sign in.</p>
      </div>

      <!-- Method selector -->
      <div class="ar-method-tabs mb-4" role="tablist">
        <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="ar-method-tab"
            :class="{ active: method === tab.key }"
            @click="selectMethod(tab.key)"
        >
          <i :class="tab.icon" class="me-2"></i>{{ tab.label }}
        </button>
      </div>

      <!-- Account locked banner -->
      <div v-if="isLocked" class="alert alert-danger d-flex align-items-start gap-2 py-3" role="alert">
        <span style="font-size:1.1rem;">🔒</span>
        <div>
          <strong>Account locked</strong><br/>
          <span style="font-size:0.9rem;">{{ errorMessage }}</span>
        </div>
      </div>

      <!-- Cooldown banner -->
      <div v-else-if="cooldownSecondsRemaining > 0" class="alert alert-warning d-flex align-items-start gap-2 py-3" role="alert">
        <span style="font-size:1.1rem;">⏱</span>
        <div>
          <strong>Too many failed attempts</strong><br/>
          <span style="font-size:0.9rem;">
            Please wait <strong>{{ formatCooldown(cooldownSecondsRemaining) }}</strong> before trying again.
          </span>
        </div>
      </div>

      <!-- Neutral OTP info -->
      <div v-if="infoMessage" class="alert alert-info py-2" role="status">{{ infoMessage }}</div>

      <!-- Generic error -->
      <div v-if="errorMessage && !isLocked && cooldownSecondsRemaining === 0" class="alert alert-danger py-2" role="alert">
        {{ errorMessage }}
        <div v-if="notRegistered" class="mt-2">
          <a href="/signup/driver" class="btn btn-sm ar-btn-brand">Sign up to drive</a>
        </div>
      </div>

      <!-- Password login -->
      <form v-if="method === 'password'" @submit.prevent="handlePasswordLogin">
        <div class="mb-3">
          <label class="form-label">Email address</label>
          <input class="form-control" type="email" v-model="email" required placeholder="email@address.com" @input="clearGenericError"/>
        </div>
        <div class="mb-3">
          <label class="form-label">Password</label>
          <div class="position-relative">
            <input class="form-control" :type="showPassword ? 'text' : 'password'" v-model="password" required
                   placeholder="*********" autocomplete="current-password" @input="clearGenericError"/>
            <button type="button" class="ar-show-hide" @click="showPassword = !showPassword">
              <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
            </button>
          </div>
        </div>
        <button class="btn ar-btn-brand w-100" type="submit" :disabled="loading || isLocked || cooldownSecondsRemaining > 0">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          {{ loading ? 'Signing in...' : 'Sign in' }}
        </button>
      </form>

      <!-- OTP login (phone or email) -->
      <form v-else @submit.prevent="otpStage === 'request' ? handleSendOtp() : handleVerifyOtp()">
        <template v-if="otpStage === 'request'">
          <div class="mb-3">
            <label class="form-label">{{ method === 'phone' ? 'Phone number' : 'Email address' }}</label>
            <div v-if="method === 'phone'" class="input-group">
              <select class="form-select" style="max-width: 120px;" v-model="phoneCountryCode">
                <option value="+1">+1 (US)</option>
                <option value="+1">+1 (CA)</option>
              </select>
              <input
                  class="form-control"
                  type="tel"
                  v-model="identifier"
                  required
                  placeholder="5551234567"
                  @input="clearGenericError"
              />
            </div>
            <input
                v-else
                class="form-control"
                type="email"
                v-model="identifier"
                required
                placeholder="email@address.com"
                @input="clearGenericError"
            />
          </div>
          <button class="btn ar-btn-brand w-100" type="submit" :disabled="loading || isLocked || cooldownSecondsRemaining > 0">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loading ? 'Sending...' : 'Send verification code' }}
          </button>
        </template>

        <template v-else>
          <div class="mb-3">
            <label class="form-label">Verification code</label>
            <input class="form-control" type="text" inputmode="numeric" v-model="otpCode" required
                   placeholder="Enter the code we sent" @input="clearGenericError"/>
            <div class="form-text">
              Sent to {{ loginIdentifier }}.
              <a href="#" @click.prevent="resetOtp">Use a different {{ method === 'phone' ? 'number' : 'email' }}</a>
            </div>
          </div>
          <button class="btn ar-btn-brand w-100" type="submit" :disabled="loading || isLocked || cooldownSecondsRemaining > 0">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loading ? 'Verifying...' : 'Verify & sign in' }}
          </button>
        </template>
      </form>

      <p class="text-center text-muted mt-4 mb-0">
        Don't have an account?
        <a href="/signup/driver" class="ar-link fw-semibold">Sign up</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue';

const tabs = [
  { key: 'phone', label: 'Phone', icon: 'fa fa-mobile-screen' },
  { key: 'email', label: 'Email', icon: 'fa fa-envelope' },
  { key: 'password', label: 'Password', icon: 'fa fa-lock' },
];

const method = ref('phone');
const otpStage = ref('request');

const identifier = ref('');
const phoneCountryCode = ref('+1');

// The full identifier sent to the server: phone numbers are prefixed with the selected
// country code (E.164, matching the signup flow); emails are used as-is.
const loginIdentifier = computed(() =>
    method.value === 'phone' ? `${phoneCountryCode.value}${identifier.value}` : identifier.value
);
const otpCode = ref('');
const email = ref('');
const password = ref('');
const showPassword = ref(false);

const loading = ref(false);
const errorMessage = ref(null);
const infoMessage = ref(null);
// Set when the identifier has no driver account, so the error banner can offer the signup page.
const notRegistered = ref(false);
const isLocked = ref(false);
const cooldownSecondsRemaining = ref(0);

let cooldownInterval = null;

const selectMethod = (key) => {
  method.value = key;
  resetOtp();
  errorMessage.value = null;
  infoMessage.value = null;
  notRegistered.value = false;
};

const resetOtp = () => {
  otpStage.value = 'request';
  otpCode.value = '';
  infoMessage.value = null;
};

const clearGenericError = () => {
  if (!isLocked.value && cooldownSecondsRemaining.value === 0) {
    errorMessage.value = null;
    notRegistered.value = false;
  }
};

const formatCooldown = (seconds) => {
  if (seconds <= 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
};

const startCooldownCountdown = (seconds) => {
  clearInterval(cooldownInterval);
  cooldownSecondsRemaining.value = seconds;
  cooldownInterval = setInterval(() => {
    if (cooldownSecondsRemaining.value <= 1) {
      cooldownSecondsRemaining.value = 0;
      clearInterval(cooldownInterval);
      cooldownInterval = null;
    } else {
      cooldownSecondsRemaining.value--;
    }
  }, 1000);
};

// Handles the shared lock (403) and cooldown (429) responses the auth endpoints can return.
// Returns true if the response was a lock/cooldown and has been handled.
const handleRateLimitResponse = async (response) => {
  if (response.status === 403) {
    isLocked.value = true;
    errorMessage.value = (await response.text()) || 'Your account has been locked. Please contact support.';
    return true;
  }
  if (response.status === 429) {
    const retryAfter = parseInt(response.headers.get('Retry-After'), 10);
    startCooldownCountdown(retryAfter > 0 ? retryAfter : 30);
    await response.text();
    return true;
  }
  return false;
};

// Navigates to the server-provided post-login URL. Uses an explicit navigation (rather than
// following a fetch redirect) so the session cookie set on this response is committed first.
const goToRedirect = async (response) => {
  const data = await response.json();
  window.location.assign(data.redirectUrl || '/driver/application-status');
};

const handlePasswordLogin = async () => {
  if (loading.value || isLocked.value || cooldownSecondsRemaining.value > 0) return;
  loading.value = true;
  errorMessage.value = null;
  try {
    const response = await fetch('/api/driver-portal/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: email.value, password: password.value }),
    });
    if (response.ok) {
      await goToRedirect(response);
    } else if (!(await handleRateLimitResponse(response))) {
      errorMessage.value = (await response.text()) || 'Invalid email or password.';
    }
  } catch {
    errorMessage.value = 'An error occurred. Please try again later.';
  } finally {
    loading.value = false;
  }
};

const handleSendOtp = async () => {
  if (loading.value || isLocked.value || cooldownSecondsRemaining.value > 0) return;
  loading.value = true;
  errorMessage.value = null;
  infoMessage.value = null;
  notRegistered.value = false;
  try {
    const response = await fetch('/api/driver-portal/send-login-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIdentifier: loginIdentifier.value, isEmail: method.value === 'email' }),
    });
    if (response.ok) {
      otpStage.value = 'verify';
      infoMessage.value = `We've sent a verification code to ${loginIdentifier.value}.`;
    } else if (!(await handleRateLimitResponse(response))) {
      // 404 is the server saying no driver is registered with this identifier.
      notRegistered.value = response.status === 404;
      errorMessage.value = (await response.text()) || 'Unable to send a code. Please check the details and try again.';
    }
  } catch {
    errorMessage.value = 'An error occurred. Please try again later.';
  } finally {
    loading.value = false;
  }
};

const handleVerifyOtp = async () => {
  if (loading.value || isLocked.value || cooldownSecondsRemaining.value > 0) return;
  loading.value = true;
  errorMessage.value = null;
  try {
    const response = await fetch('/api/driver-portal/authenticate-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIdentifier: loginIdentifier.value, isEmail: method.value === 'email', otpCode: otpCode.value }),
    });
    if (response.ok) {
      await goToRedirect(response);
    } else if (!(await handleRateLimitResponse(response))) {
      errorMessage.value = (await response.text()) || 'Invalid code, please try again.';
    }
  } catch {
    errorMessage.value = 'An error occurred. Please try again later.';
  } finally {
    loading.value = false;
  }
};

onUnmounted(() => clearInterval(cooldownInterval));
</script>

<style scoped>
.ar-login-page {
  --ar-purple: #4f3594;
  --ar-blue: #0983c0;
  --ar-ink: #231f20;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, var(--ar-purple) 0%, var(--ar-blue) 100%);
}

.ar-login-card {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 1.25rem;
  padding: 2.5rem 2rem;
  box-shadow: 0 1.5rem 3rem rgba(35, 31, 32, 0.25);
}

.ar-login-logo {
  height: 48px;
  width: auto;
}

.ar-login-title {
  color: var(--ar-ink);
  font-weight: 700;
}

.ar-method-tabs {
  display: flex;
  gap: 0.25rem;
  background: #f1f1f4;
  border-radius: 0.75rem;
  padding: 0.25rem;
}

.ar-method-tab {
  flex: 1;
  border: 0;
  background: transparent;
  padding: 0.55rem 0.5rem;
  border-radius: 0.55rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #6c6a72;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.ar-method-tab.active {
  background: #fff;
  color: var(--ar-purple);
  box-shadow: 0 1px 3px rgba(35, 31, 32, 0.15);
}

.ar-btn-brand {
  background: linear-gradient(135deg, var(--ar-purple) 0%, var(--ar-blue) 100%);
  border: 0;
  color: #fff;
  font-weight: 600;
  padding: 0.65rem 1rem;
  border-radius: 0.65rem;
}

.ar-btn-brand:hover:not(:disabled) {
  filter: brightness(1.07);
  color: #fff;
}

.ar-btn-brand:disabled {
  opacity: 0.65;
}

.ar-show-hide {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #8a888f;
  padding: 0.25rem 0.5rem;
}

.ar-link,
.ar-login-card a:not(.btn) {
  color: var(--ar-blue);
  text-decoration: none;
}

.ar-link:hover,
.ar-login-card a:not(.btn):hover {
  text-decoration: underline;
}

.form-control:focus {
  border-color: var(--ar-purple);
  box-shadow: 0 0 0 0.2rem rgba(79, 53, 148, 0.15);
}
</style>
